import { PublicKey } from '@solana/web3.js';

import type { AddSolanaPairRequest } from 'entities/project/api/project/api.types';

export type SolanaPairFormValues = {
  poolAddress: string;
  baseMint: string;
  baseSymbol: string;
  baseDecimals: string;
  quoteMint: string;
  quoteSymbol: string;
  quoteDecimals: string;
  notes: string;
};

export type SolanaPairFormErrors = Partial<Record<keyof SolanaPairFormValues, string>>;

export const initialSolanaPairFormValues: SolanaPairFormValues = {
  poolAddress: '',
  baseMint: '',
  baseSymbol: '',
  baseDecimals: '',
  quoteMint: '',
  quoteSymbol: '',
  quoteDecimals: '',
  notes: '',
};

const isCanonicalSolanaAddress = (value: string) => {
  try {
    return new PublicKey(value).toBase58() === value;
  } catch {
    return false;
  }
};

const isValidDecimals = (value: string) => {
  const decimals = Number(value);
  return Number.isInteger(decimals) && decimals >= 0 && decimals <= 255;
};

export const validateSolanaPairForm = (values: SolanaPairFormValues): SolanaPairFormErrors => {
  const errors: SolanaPairFormErrors = {};
  const poolAddress = values.poolAddress.trim();
  const baseMint = values.baseMint.trim();
  const quoteMint = values.quoteMint.trim();
  const baseSymbol = values.baseSymbol.trim();
  const quoteSymbol = values.quoteSymbol.trim();

  if (!isCanonicalSolanaAddress(poolAddress)) {
    errors.poolAddress = 'Enter a canonical Solana address';
  }
  if (!isCanonicalSolanaAddress(baseMint)) {
    errors.baseMint = 'Enter a canonical Solana address';
  }
  if (!isCanonicalSolanaAddress(quoteMint)) {
    errors.quoteMint = 'Enter a canonical Solana address';
  }
  if (baseMint && quoteMint && baseMint === quoteMint) {
    errors.baseMint = 'Base and quote mints must differ';
  }
  if (!baseSymbol || baseSymbol.length > 32) {
    errors.baseSymbol = 'Use a symbol from 1 to 32 characters';
  }
  if (!quoteSymbol || quoteSymbol.length > 32) {
    errors.quoteSymbol = 'Use a symbol from 1 to 32 characters';
  }
  if (!isValidDecimals(values.baseDecimals.trim())) {
    errors.baseDecimals = 'Use an integer from 0 to 255';
  }
  if (!isValidDecimals(values.quoteDecimals.trim())) {
    errors.quoteDecimals = 'Use an integer from 0 to 255';
  }
  if (values.notes.trim().length > 128) {
    errors.notes = 'Notes must not exceed 128 characters';
  }

  return errors;
};

export const toAddSolanaPairRequest = (
  projectId: number,
  values: SolanaPairFormValues,
): AddSolanaPairRequest => {
  const notes = values.notes.trim();
  return {
    projectId,
    dex: 'raydium_amm_v4:solana',
    pool_address: values.poolAddress.trim(),
    base_mint: values.baseMint.trim(),
    base_symbol: values.baseSymbol.trim(),
    base_decimals: Number(values.baseDecimals),
    quote_mint: values.quoteMint.trim(),
    quote_symbol: values.quoteSymbol.trim(),
    quote_decimals: Number(values.quoteDecimals),
    ...(notes ? { notes } : {}),
  };
};
